import { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const SubscriptionGate = ({ children, onSignOut, onUpgrade, onDeleteAccount }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);

  const checkSubscription = useCallback(async () => {
    try {
      const res = await fetch(`${API}/subscriptions/status`, {
        headers: authHeaders(),
      });
      if (res.status === 401) {
        // Token is invalid or expired — sign out and return to landing
        setLoading(false);
        if (onSignOut) onSignOut();
        return;
      }
      if (!res.ok) {
        setStatus({ status: 'error' });
        setLoading(false);
        return;
      }
      const data = await res.json();
      setStatus(data);
    } catch (e) {
      console.error('Subscription check failed:', e);
      setStatus({ status: 'error' });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Check for Stripe redirect params
    const params = new URLSearchParams(window.location.search);
    const subResult = params.get('subscription');
    if (subResult === 'success') {
      setSuccessMessage('Welcome back. Eunoia is ready.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      // Re-check subscription after a brief delay (webhook may still be processing)
      const timer = setTimeout(() => {
        checkSubscription().then(() => {
          // Auto-dismiss after subscription confirmed
          setTimeout(() => setSuccessMessage(null), 1200);
        });
      }, 1500);
      return () => clearTimeout(timer);
    } else if (subResult === 'canceled') {
      window.history.replaceState({}, '', window.location.pathname);
      checkSubscription();
    } else {
      checkSubscription();
    }
  }, [checkSubscription]);

  // Loading state — blank to avoid flash
  if (loading) return null;

  // Success message overlay (briefly shown after returning from Stripe)
  if (successMessage) {
    return (
      <div className="velvet-void" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 32px',
      }} data-testid="subscription-success">
        <div style={{ textAlign: 'center' }} className="fade-in">
          <div className="breathing-sphere" style={{ margin: '0 auto 40px' }} />
          <p style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '28px',
            color: 'rgba(255, 243, 225, 0.82)',
          }}>
            {successMessage}
          </p>
        </div>
      </div>
    );
  }

  // Allow access if active subscription or active trial
  if (status && (status.status === 'active' || status.status === 'trialing')) {
    return children;
  }

  // Trial expired — velvet paywall with upgrade + delete option
  if (status && status.status === 'trial_expired') {
    return (
      <div className="velvet-void" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 32px',
      }} data-testid="trial-expired-gate">
        <div style={{ width: '100%', maxWidth: '400px', textAlign: 'center' }} className="fade-in">
          <div className="breathing-sphere" style={{ margin: '0 auto 48px' }} />

          <h2 style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '26px',
            fontWeight: 400,
            color: 'rgba(255, 243, 225, 0.82)',
            marginBottom: '16px',
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
          }}>
            Your trial has ended.
          </h2>

          <p style={{
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '17px',
            color: 'rgba(255, 243, 225, 0.55)',
            lineHeight: 1.7,
            marginBottom: '40px',
          }}>
            Your conversations are still here.<br />
            Continue for $7.99/month — cancel anytime.
          </p>

          <button
            onClick={onUpgrade}
            className="velvet-btn"
            data-testid="trial-expired-upgrade-btn"
            style={{
              maxWidth: '280px',
              margin: '0 auto',
              display: 'block',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '17px',
              letterSpacing: '0.02em',
            }}
          >
            Continue — $7.99/month
          </button>

          <div style={{
            width: '40px',
            height: '1px',
            background: 'rgba(196, 137, 74, 0.25)',
            margin: '32px auto',
          }} />

          {onDeleteAccount && (
            <button
              onClick={onDeleteAccount}
              data-testid="trial-expired-delete-btn"
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255, 243, 225, 0.25)',
                fontSize: '13px',
                fontFamily: "'EB Garamond', Georgia, serif",
                cursor: 'pointer',
                padding: '8px 16px',
                letterSpacing: '0.02em',
              }}
            >
              Or delete your profile
            </button>
          )}
        </div>
      </div>
    );
  }

  // Error state — could not verify subscription
  if (!status || status.status === 'error') {
    return (
      <div className="velvet-void" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 32px',
      }}>
        <div style={{ textAlign: 'center' }} className="fade-in">
          <p style={{
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '17px',
            color: 'rgba(255, 243, 225, 0.65)',
            lineHeight: 1.7,
            marginBottom: '24px',
          }}>
            Unable to verify subscription.<br />Please try again.
          </p>
          <button
            onClick={() => { setLoading(true); checkSubscription(); }}
            className="velvet-btn"
            style={{
              maxWidth: '280px',
              margin: '0 auto',
              display: 'block',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '17px',
            }}
          >
            try again
          </button>
        </div>
      </div>
    );
  }

  // Paywall — warm honest invitation
  return (
    <div className="velvet-void" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 32px',
    }} data-testid="subscription-gate">
      <div style={{ width: '100%', maxWidth: '400px', textAlign: 'center' }} className="fade-in">
        <div className="breathing-sphere" style={{ margin: '0 auto 48px' }} />

        <h2 style={{
          fontFamily: "'Cormorant', Georgia, serif",
          fontStyle: 'italic',
          fontSize: '28px',
          fontWeight: 400,
          color: 'rgba(255, 243, 225, 0.82)',
          marginBottom: '20px',
          letterSpacing: '-0.01em',
          lineHeight: 1.3,
        }} data-testid="gate-title">
          Eunoia Presence
        </h2>

        <p style={{
          fontFamily: "'EB Garamond', Georgia, serif",
          fontSize: '17px',
          color: 'rgba(255, 243, 225, 0.65)',
          lineHeight: 1.7,
          marginBottom: '40px',
        }} data-testid="gate-description">
          7 days free, then $7.99/month<br />
          cancel anytime
        </p>

        <button
          onClick={onUpgrade}
          className="velvet-btn"
          data-testid="upgrade-btn"
          style={{
            maxWidth: '280px',
            margin: '0 auto',
            display: 'block',
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '17px',
            letterSpacing: '0.02em',
          }}
        >
          continue with Eunoia
        </button>

        {onSignOut && (
          <button
            onClick={onSignOut}
            data-testid="gate-signout-btn"
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 243, 225, 0.35)',
              fontSize: '13px',
              fontFamily: "'EB Garamond', Georgia, serif",
              marginTop: '28px',
              cursor: 'pointer',
              padding: '8px 16px',
            }}
          >
            or sign out
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionGate;
